import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, Globe, Building2, LogOut, ChevronRight,
  Power, X, Loader2, Sun, Moon, MessageSquare, Trash2, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { useLanguage } from '../hooks/useTranslation';
import { useThemeStore } from '../store/themeStore';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { api } from '../config/api';
import logoMark from '../assets/logo-mark.png';
import { TENANT_TYPES } from '../constants/tenantTypes';

interface TenantRow {
  id: string;
  code: string | null;
  nom: string;
  type: string;
  email: string | null;
  devise: string;
  actif: boolean;
  sms_credits_restants: number;
  whatsapp_credits_restants: number;
}

interface QuotaForm {
  sms_credits: string;
  prix_par_sms: string;
  whatsapp_credits: string;
  prix_par_whatsapp: string;
}

interface TenantRecap {
  nom: string;
  total_users: number;
  total_colis: number;
  total_livreurs: number;
  colis_en_cours: number;
  livraisons_en_attente: number;
  factures_impayees: number;
}


const TENANT_TYPE_LABELS: Record<string, { fr: string; en: string }> = Object.fromEntries(
  TENANT_TYPES.map((t) => [t.value, { fr: t.fr, en: t.en }]),
);

const EMPTY_QUOTA: QuotaForm = { sms_credits: '', prix_par_sms: '10', whatsapp_credits: '', prix_par_whatsapp: '20' };

export const TenantSelector: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setActiveTenant, logout } = useAuthStore();
  const { language } = useLanguage();
  const { theme, toggleTheme } = useThemeStore();
  const isEn = language === 'en';
  const dark = theme === 'dark';

  const [search, setSearch] = useState('');
  const [quotaTenantId, setQuotaTenantId] = useState<string | null>(null);
  const [quotaForm, setQuotaForm] = useState<QuotaForm>(EMPTY_QUOTA);
  const [recapTenantId, setRecapTenantId] = useState<string | null>(null);

  // ── Theme-aware classes ────────────────────────────────────────────────────
  const rootBg   = dark ? 'bg-[#0a0515]'   : 'bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50';
  const hdrBg    = dark ? 'bg-white/5 border-white/10'  : 'bg-white/90 backdrop-blur-xl border-slate-200';
  const txtPrim  = dark ? 'text-white'      : 'text-slate-900';
  const txtSec   = dark ? 'text-white/50'   : 'text-slate-500';
  const txtMuted = dark ? 'text-white/30'   : 'text-slate-400';
  const cardBase = dark
    ? 'bg-white/5 border-white/[0.08] hover:border-white/20 hover:bg-white/[0.08]'
    : 'bg-white border-slate-200 hover:border-violet-300 hover:shadow-sm';
  const cardInact= dark ? 'bg-white/[0.02] border-white/5 opacity-60' : 'bg-slate-50 border-slate-100 opacity-70';
  const inputCls = dark
    ? 'bg-white/5 border-white/10 text-white placeholder-white/25 focus:border-violet-500 focus:ring-violet-500/20'
    : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:ring-violet-400/20';
  const modalBg  = dark ? 'bg-[#12082a] border-white/10' : 'bg-white border-slate-200';
  const divider  = dark ? 'bg-white/10' : 'bg-slate-200';

  // ── Fetch tenants ─────────────────────────────────────────────────────────
  const { data: tenants = [], isLoading } = useQuery<TenantRow[]>({
    queryKey: ['tenants-all'],
    queryFn: async () => {
      const res = await api.get('/tenants');
      return res.data;
    },
    staleTime: 0,
    gcTime: 0,
  });

  // ── Toggle actif ──────────────────────────────────────────────────────────
  const toggleMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.patch(`/tenants/${id}/toggle-actif`);
      return res.data;
    },
    onSuccess: (updated: TenantRow) => {
      queryClient.invalidateQueries({ queryKey: ['tenants-all'] });
      toast.success(
        updated.actif
          ? (isEn ? 'Tenant reactivated' : 'Tenant réactivé')
          : (isEn ? 'Tenant suspended' : 'Tenant suspendu'),
      );
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Erreur');
    },
  });

  // ── Quota SMS/WhatsApp ─────────────────────────────────────────────────────
  const quotaMutation = useMutation({
    mutationFn: async ({ id, form }: { id: string; form: QuotaForm }) => {
      const res = await api.post(`/tenants/${id}/quota`, {
        sms_credits: parseInt(form.sms_credits) || 0,
        prix_par_sms: parseFloat(form.prix_par_sms) || 0,
        whatsapp_credits: parseInt(form.whatsapp_credits) || 0,
        prix_par_whatsapp: parseFloat(form.prix_par_whatsapp) || 0,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants-all'] });
      setQuotaTenantId(null);
      setQuotaForm(EMPTY_QUOTA);
      toast.success(isEn ? 'Credits added' : 'Crédits ajoutés');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Erreur');
    },
  });

  // ── Recap before delete ───────────────────────────────────────────────────
  const { data: recap, isFetching: recapLoading } = useQuery<TenantRecap>({
    queryKey: ['tenant-recap', recapTenantId],
    queryFn: async () => {
      const res = await api.get(`/tenants/${recapTenantId}/recap`);
      return res.data;
    },
    enabled: !!recapTenantId,
  });

  // ── Delete tenant ─────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/tenants/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants-all'] });
      setRecapTenantId(null);
      toast.success(isEn ? 'Tenant permanently deleted' : 'Tenant supprimé définitivement');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || (isEn ? 'Deletion failed' : 'Suppression échouée'));
    },
  });

  const handleDeleteWithConfirm = () => {
    if (!recap || !recapTenantId) return;
    const name = recap.nom;
    const c1 = window.confirm(
      isEn
        ? `⚠️ DANGER — You are about to permanently delete "${name}".\n\nThis action CANNOT be undone. All data will be lost.\n\nClick OK to continue.`
        : `⚠️ DANGER — Vous êtes sur le point de supprimer définitivement "${name}".\n\nCette action est IRRÉVERSIBLE. Toutes les données seront perdues.\n\nCliquez OK pour continuer.`,
    );
    if (!c1) return;
    const c2 = window.confirm(
      isEn
        ? `⚠️ SECOND CONFIRMATION\n\nTenant: ${name}\n• Users: ${recap.total_users}\n• Parcels: ${recap.total_colis} (${recap.colis_en_cours} in progress)\n• Unpaid invoices: ${recap.factures_impayees}\n\nAre you ABSOLUTELY sure you want to delete this tenant?`
        : `⚠️ DEUXIÈME CONFIRMATION\n\nTenant: ${name}\n• Utilisateurs: ${recap.total_users}\n• Colis: ${recap.total_colis} (${recap.colis_en_cours} en cours)\n• Factures impayées: ${recap.factures_impayees}\n\nÊtes-vous ABSOLUMENT sûr de vouloir supprimer ce tenant ?`,
    );
    if (!c2) return;
    const c3 = window.confirm(
      isEn
        ? `🔴 FINAL CONFIRMATION\n\nType-check: this is your LAST chance.\n\nDeleting "${name}" will permanently erase all associated data from the database. This cannot be recovered.\n\nConfirm FINAL deletion?`
        : `🔴 CONFIRMATION FINALE\n\nDernière chance : la suppression de "${name}" effacera définitivement toutes les données associées de la base de données. Impossible à récupérer.\n\nConfirmer la suppression DÉFINITIVE ?`,
    );
    if (!c3) return;
    deleteMutation.mutate(recapTenantId);
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const filtered = tenants.filter(
    (t) =>
      t.nom.toLowerCase().includes(search.toLowerCase()) ||
      (t.email || '').toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelectTenant = (tenant: TenantRow) => {
    // Journalisation best-effort — n'attend pas la réponse et ne bloque jamais la navigation
    api.post(`/tenants/${tenant.id}/enter-context`).catch(() => {});
    setActiveTenant(tenant.id, tenant.nom);
    navigate('/dashboard');
  };

  const handleGlobalView = () => {
    setActiveTenant('__all__', isEn ? 'All Tenants' : 'Tous les tenants');
    navigate('/dashboard');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const quotaTenant = quotaTenantId ? tenants.find((t) => t.id === quotaTenantId) : null;

  return (
    <div className={`min-h-screen ${rootBg} flex flex-col`}>
      {/* Header */}
      <div className={`h-16 ${hdrBg} border-b flex items-center justify-between px-6`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
            <img src={logoMark} alt="CeDo Trace" className="w-6 h-6 object-contain" />
          </div>
          <span className={`${txtPrim} font-bold`}>CEDO Trace</span>
          <span className={`${txtMuted} text-sm`}>— Super Admin</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Language switcher */}
          <LanguageSwitcher />

          {/* Theme toggle */}
          <button
            type="button"
            onClick={toggleTheme}
            title={dark ? (isEn ? 'Light mode' : 'Mode clair') : (isEn ? 'Dark mode' : 'Mode sombre')}
            className={`p-2 rounded-xl transition-all ${
              dark ? 'bg-white/10 hover:bg-white/20 text-yellow-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
            }`}
          >
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* Logout */}
          <button
            type="button"
            onClick={handleLogout}
            className={`flex items-center gap-2 ${txtSec} hover:${txtPrim} text-sm transition-colors`}
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">{isEn ? 'Logout' : 'Déconnexion'}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-4xl mx-auto w-full px-6 py-12">
        {/* Title */}
        <div className="mb-10">
          <h1 className={`text-3xl font-bold ${txtPrim} mb-2`}>
            {isEn ? 'Select a workspace' : 'Sélectionnez un espace de travail'}
          </h1>
          <p className={txtSec}>
            {isEn
              ? 'Choose an enterprise to operate as, or view the global aggregated dashboard.'
              : "Choisissez une entreprise pour opérer en son nom, ou consultez le tableau de bord global agrégé."}
          </p>
          <button
            type="button"
            onClick={() => navigate('/super-admin/config')}
            className="text-violet-400 hover:text-violet-300 text-sm mt-2 transition-colors"
          >
            {isEn ? 'Create a new enterprise →' : 'Créer une nouvelle entreprise →'}
          </button>
        </div>

        {/* Global view card */}
        <button
          type="button"
          onClick={handleGlobalView}
          className={`w-full mb-6 p-5 rounded-2xl
            bg-gradient-to-r from-violet-600/20 to-indigo-600/20
            border border-violet-500/30 hover:border-violet-400/60
            flex items-center gap-4 text-left transition-all hover:scale-[1.01] group`}
        >
          <div className="w-12 h-12 rounded-xl bg-violet-600/30 flex items-center justify-center flex-shrink-0">
            <Globe className="w-6 h-6 text-violet-400" />
          </div>
          <div className="flex-1">
            <p className={`${txtPrim} font-semibold`}>
              {isEn ? 'Global Dashboard' : 'Tableau de Bord Global'}
            </p>
            <p className={`${txtSec} text-sm mt-0.5`}>
              {isEn
                ? `Aggregated analytics across all ${tenants.length} tenants`
                : `Analyses agrégées pour tous les ${tenants.length} tenants`}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-violet-400 group-hover:translate-x-1 transition-transform" />
        </button>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-6">
          <div className={`flex-1 h-px ${divider}`} />
          <span className={`${txtMuted} text-xs uppercase tracking-widest`}>
            {isEn ? 'or select an enterprise' : 'ou sélectionnez une entreprise'}
          </span>
          <div className={`flex-1 h-px ${divider}`} />
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${txtMuted}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isEn ? 'Search tenants…' : 'Rechercher un tenant…'}
            className={`w-full pl-10 pr-4 py-3 border rounded-xl text-sm outline-none focus:ring-2 transition-all ${inputCls}`}
          />
        </div>

        {/* Tenant list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((tenant) => {
              const typeLabel = TENANT_TYPE_LABELS[tenant.type];
              return (
                <div
                  key={tenant.id}
                  className={`w-full p-4 rounded-xl border flex items-center gap-4 transition-all ${
                    tenant.actif ? cardBase : cardInact
                  }`}
                >
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    dark ? 'border border-white/10' : 'border border-slate-200'
                  } ${tenant.actif
                      ? 'bg-gradient-to-br from-violet-600/40 to-indigo-600/40'
                      : (dark ? 'bg-white/5' : 'bg-slate-100')
                  }`}>
                    <span className={`${tenant.actif ? 'text-white' : (dark ? 'text-white/50' : 'text-slate-400')} font-bold text-sm`}>
                      {tenant.nom.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`${txtPrim} font-medium truncate`}>{tenant.nom}</span>
                      {tenant.code && (
                        <span className="text-xs font-mono text-white/30">{tenant.code}</span>
                      )}
                      {!tenant.actif && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/20">
                          {isEn ? 'Suspended' : 'Suspendu'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {tenant.email && (
                        <span className={`${txtSec} text-xs truncate`}>{tenant.email}</span>
                      )}
                      {typeLabel && (
                        <span className={`${txtMuted} text-xs`}>
                          · {isEn ? typeLabel.en : typeLabel.fr}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: credits + actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Credits display */}
                    <div className="hidden md:flex items-center gap-1 text-xs">
                      <span className={`px-2 py-1 rounded-lg ${dark ? 'bg-white/5 text-white/40' : 'bg-slate-100 text-slate-500'}`}>
                        📱 {tenant.sms_credits_restants}
                      </span>
                      <span className={`px-2 py-1 rounded-lg ${dark ? 'bg-white/5 text-white/40' : 'bg-slate-100 text-slate-500'}`}>
                        💬 {tenant.whatsapp_credits_restants}
                      </span>
                    </div>

                    {/* Add SMS credits */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setQuotaTenantId(tenant.id); setQuotaForm(EMPTY_QUOTA); }}
                      title={isEn ? 'Add SMS/WhatsApp credits' : 'Ajouter des crédits SMS/WhatsApp'}
                      className={`p-1.5 rounded-lg transition-colors ${
                        dark
                          ? 'text-blue-400/60 hover:text-blue-400 hover:bg-blue-400/10'
                          : 'text-blue-500/60 hover:text-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                    </button>

                    {/* Toggle suspend */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleMutation.mutate(tenant.id); }}
                      disabled={toggleMutation.isPending}
                      title={tenant.actif ? (isEn ? 'Suspend' : 'Suspendre') : (isEn ? 'Reactivate' : 'Réactiver')}
                      className={`p-1.5 rounded-lg transition-colors ${
                        tenant.actif
                          ? (dark ? 'text-yellow-400/60 hover:text-yellow-400 hover:bg-yellow-400/10' : 'text-yellow-500/60 hover:text-yellow-500 hover:bg-yellow-50')
                          : (dark ? 'text-green-400/60 hover:text-green-400 hover:bg-green-400/10' : 'text-green-500/60 hover:text-green-500 hover:bg-green-50')
                      }`}
                    >
                      <Power className="w-3.5 h-3.5" />
                    </button>

                    {/* Delete tenant */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setRecapTenantId(tenant.id); }}
                      title={isEn ? 'Delete tenant' : 'Supprimer le tenant'}
                      className={`p-1.5 rounded-lg transition-colors ${
                        dark
                          ? 'text-red-400/50 hover:text-red-400 hover:bg-red-400/10'
                          : 'text-red-400/60 hover:text-red-500 hover:bg-red-50'
                      }`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>

                    {/* Select / Enter */}
                    {tenant.actif && (
                      <button
                        type="button"
                        onClick={() => handleSelectTenant(tenant)}
                        title={isEn ? 'Enter workspace' : 'Accéder à l\'espace'}
                        className={`p-1.5 rounded-lg transition-colors ${
                          dark ? 'text-white/30 hover:text-white/70 hover:bg-white/10' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className={`text-center py-12 ${txtMuted}`}>
                <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>{isEn ? 'No tenants found' : 'Aucun tenant trouvé'}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Quota SMS Modal ──────────────────────────────────────────────────── */}
      {quotaTenantId && quotaTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className={`w-full max-w-md ${modalBg} border rounded-2xl p-6 shadow-2xl`}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className={`${txtPrim} font-bold text-lg`}>
                  {isEn ? 'Add credits' : 'Ajouter des crédits'}
                </h2>
                <p className={`${txtSec} text-sm mt-0.5`}>{quotaTenant.nom}</p>
              </div>
              <button type="button" onClick={() => setQuotaTenantId(null)} className={`${txtSec} hover:${txtPrim}`}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Current credits */}
            <div className={`flex gap-3 mb-5 p-3 rounded-xl ${dark ? 'bg-white/5' : 'bg-slate-50'}`}>
              <div className="flex-1 text-center">
                <p className={`text-xs ${txtMuted} mb-1`}>📱 SMS</p>
                <p className={`font-bold ${txtPrim}`}>{quotaTenant.sms_credits_restants}</p>
              </div>
              <div className={`w-px ${divider}`} />
              <div className="flex-1 text-center">
                <p className={`text-xs ${txtMuted} mb-1`}>💬 WhatsApp</p>
                <p className={`font-bold ${txtPrim}`}>{quotaTenant.whatsapp_credits_restants}</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* SMS */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block ${txtSec} text-xs mb-1.5`}>
                    {isEn ? 'SMS credits' : 'Crédits SMS'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={quotaForm.sms_credits}
                    onChange={(e) => setQuotaForm((f) => ({ ...f, sms_credits: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-xl text-sm outline-none focus:ring-2 ${inputCls}`}
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className={`block ${txtSec} text-xs mb-1.5`}>
                    {isEn ? 'Price/SMS' : 'Prix/SMS'} ({quotaTenant.devise || 'FCFA'})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={quotaForm.prix_par_sms}
                    onChange={(e) => setQuotaForm((f) => ({ ...f, prix_par_sms: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-xl text-sm outline-none focus:ring-2 ${inputCls}`}
                    placeholder="10"
                  />
                </div>
              </div>

              {/* WhatsApp */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block ${txtSec} text-xs mb-1.5`}>
                    {isEn ? 'WhatsApp credits' : 'Crédits WhatsApp'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={quotaForm.whatsapp_credits}
                    onChange={(e) => setQuotaForm((f) => ({ ...f, whatsapp_credits: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-xl text-sm outline-none focus:ring-2 ${inputCls}`}
                    placeholder="50"
                  />
                </div>
                <div>
                  <label className={`block ${txtSec} text-xs mb-1.5`}>
                    {isEn ? 'Price/msg' : 'Prix/msg'} ({quotaTenant.devise || 'FCFA'})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={quotaForm.prix_par_whatsapp}
                    onChange={(e) => setQuotaForm((f) => ({ ...f, prix_par_whatsapp: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-xl text-sm outline-none focus:ring-2 ${inputCls}`}
                    placeholder="20"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setQuotaTenantId(null)}
                className={`flex-1 py-2.5 rounded-xl border ${dark ? 'border-white/10 text-white/60 hover:text-white/90' : 'border-slate-200 text-slate-500 hover:text-slate-800'} text-sm transition-colors`}
              >
                {isEn ? 'Cancel' : 'Annuler'}
              </button>
              <button
                type="button"
                onClick={() => quotaMutation.mutate({ id: quotaTenantId, form: quotaForm })}
                disabled={
                  quotaMutation.isPending ||
                  (!parseInt(quotaForm.sms_credits) && !parseInt(quotaForm.whatsapp_credits))
                }
                className="flex-1 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm
                  disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {quotaMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEn ? 'Add credits' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Tenant Recap Modal ─────────────────────────────────────────── */}
      {recapTenantId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className={`w-full max-w-md ${modalBg} border border-red-500/30 rounded-2xl p-6 shadow-2xl`}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h2 className="font-bold text-red-400 text-lg">
                    {isEn ? 'Delete tenant' : 'Supprimer le tenant'}
                  </h2>
                  <p className={`${txtSec} text-xs`}>
                    {isEn ? 'Irreversible action' : 'Action irréversible'}
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setRecapTenantId(null)} title={isEn ? 'Close' : 'Fermer'} className={`${txtSec} hover:${txtPrim}`}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {recapLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
              </div>
            ) : recap ? (
              <>
                <p className={`${txtSec} text-sm mb-4`}>
                  {isEn
                    ? `You are about to permanently delete "${recap.nom}". Review the data that will be lost:`
                    : `Vous êtes sur le point de supprimer définitivement "${recap.nom}". Voici les données qui seront perdues :`}
                </p>

                <div className={`rounded-xl ${dark ? 'bg-white/5' : 'bg-slate-50'} p-4 space-y-2.5 mb-5`}>
                  {[
                    { label: isEn ? 'Users' : 'Utilisateurs', value: recap.total_users, warn: recap.total_users > 0 },
                    { label: isEn ? 'Couriers' : 'Livreurs', value: recap.total_livreurs, warn: recap.total_livreurs > 0 },
                    { label: isEn ? 'Total parcels' : 'Colis total', value: recap.total_colis, warn: false },
                    { label: isEn ? 'Parcels in progress' : 'Colis en cours', value: recap.colis_en_cours, warn: recap.colis_en_cours > 0 },
                    { label: isEn ? 'Active deliveries' : 'Livraisons en cours', value: recap.livraisons_en_attente, warn: recap.livraisons_en_attente > 0 },
                    { label: isEn ? 'Unpaid invoices' : 'Factures impayées', value: recap.factures_impayees, warn: recap.factures_impayees > 0 },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between">
                      <span className={`${txtSec} text-sm`}>{row.label}</span>
                      <span className={`font-semibold text-sm ${row.warn && row.value > 0 ? 'text-red-400' : txtPrim}`}>
                        {row.value}
                        {row.warn && row.value > 0 && ' ⚠️'}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setRecapTenantId(null)}
                    className={`flex-1 py-2.5 rounded-xl border ${dark ? 'border-white/10 text-white/60 hover:text-white/90' : 'border-slate-200 text-slate-500 hover:text-slate-800'} text-sm transition-colors`}
                  >
                    {isEn ? 'Cancel' : 'Annuler'}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteWithConfirm}
                    disabled={deleteMutation.isPending}
                    className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold text-sm
                      disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                    <Trash2 className="w-4 h-4" />
                    {isEn ? 'Delete permanently' : 'Supprimer définitivement'}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

    </div>
  );
};
